import { isEmpty, isNil, isObjectLike } from 'lodash-es';
import { ColumnState, FirstDataRenderedEvent, ValueFormatterParams } from '@ag-grid-community/core';

import { convertToDollar } from 'src/features/shared/utils/utils';
import type { BodItem, CombinedResult, GroupedLotData, GroupedManufacturerData, TableItem } from './types';
import { getUsdFormattedString } from '../../shared/utils';
import { SortPayload } from 'src/features/Dashboard/queries/apiTypes';
import { LotsResponse } from '../../shared/types';

/*
* This uses breadth first search traversal to find overlapping manufacturers through lotIds
* and return a combined data set. As Manufacturer and LotIds share a many to many relation.
* Payload Eg:
Here map keys are Manufacturer Names like(Name1 and son on)
 groupedManufacturers: new Map([
    ["Name1", { bods: [...someConnectedBods], lotIds: new Set([1, 2]) }],
    ["Name2", { bods: [...someConnectedBods], lotIds: new Set([1, 3]) }],
    ["Name3", { bods: [...someConnectedBods], lotIds: new Set([3, 4]) }],
    ["Name4", { bods: [...someConnectedBods], lotIds: new Set([5]) }]
]);

Here map keys are Lot ids like(1, 2, 3 and so) and values as Set of Manufacturers connected to it.
const groupedLotIds = new Map([
    [1, new Set(["Name1", "Name2"])],
    [2, new Set(["Name1"])],
    [3, new Set(["Name2", "Name3"])],
    [4, new Set(["Name3"])],
    [5, new Set(["Name4"])]
]);

The function will give us result as follows, where Name1, Name3 and Name3 manufacturers
and 1, 2, 3 and 4 lots ids are part of single group.
Similarly: Name 4 and lotId 5 is part of other group.
[
    {
        manufacturers: [
            { manufacturer: "Name1", bods: [...someConnectedBods] },
            { manufacturer: "Name2", bods: [...someConnectedBods] },
            { manufacturer: "Name3", bods: [...someConnectedBods] }
        ],
        lotIds: [1, 2, 3, 4]
    },
    {
        manufacturers: [{ manufacturer: "Name4", bods: [...someConnectedBods] }],
        lotIds: [5]
    }
]
*/
export const getOverlappingManufacturersWithLots = (groupedManufacturers: Map<string, GroupedManufacturerData>, groupedLotIds: Map<number, GroupedLotData>) => {
    const visitedManufacturers = new Set<string>();
    const visitedLotIds = new Set<number>();
    const combinedResult: Array<CombinedResult> = [];

    for (const manufacturerKey of groupedManufacturers.keys()) {
        // we already processed this manufacturer and it's connections
        if (visitedManufacturers.has(manufacturerKey)) continue;

        const connectedManufacturers: CombinedResult['manufacturers'] = [];
        const connectedLotIds: CombinedResult['lotIds'] = [];
        const queue = [manufacturerKey];

        visitedManufacturers.add(manufacturerKey);

        // This loop uses breadth-first traversal to find all the connected manufacutrers
        // of manufacturerKey, through the shared/overlapping lotIds, and put them all in 1 result object.
        for (let idx = 0; idx < queue.length; idx++) {
            const manufacturer = queue[idx];

            const manufacturerData = groupedManufacturers.get(manufacturer);
            if (!manufacturerData) continue;

            connectedManufacturers.push({ manufacturer, bods: manufacturerData.bods });

            // get all the other connected manufacturers of current manufacturer through lotIds.
            manufacturerData.lotIds.forEach((lotId) => {
                // In case we are encountering already iterated/processed lotId, skp this iteration.
                if (visitedLotIds.has(lotId)) return;
                visitedLotIds.add(lotId);

                const groupedLotIdSet = groupedLotIds.get(lotId);
                if (!groupedLotIdSet) return;
                connectedLotIds.push(lotId);

                // Add all the connected manufacturers through the shared lotId
                groupedLotIdSet.forEach((connectedManufacturer) => {
                    // Only add the manufacturer to process in queue, if not already processed
                    if (visitedManufacturers.has(connectedManufacturer)) return;
                    visitedManufacturers.add(connectedManufacturer);

                    queue.push(connectedManufacturer);
                });
            });
        }

        // push all the connected manufacturers or lotIds for this iteration in result set.
        combinedResult.push({ manufacturers: connectedManufacturers, lotIds: connectedLotIds });
    }
    return combinedResult;
};

export const getAggregatedRowData = (combinedItem: CombinedResult, lotsMap: Map<number, LotsResponse>, kitMap: Map<number, BodItem>): TableItem => {
    const { lotIds, manufacturers } = combinedItem;
    const manufacturerNames = manufacturers.map((m) => m.manufacturer);
    const uniqueId = manufacturerNames.join('');

    const lotsAggResult = lotIds.reduce(
        (result, lotId) => {
            const lot = lotsMap.get(lotId);
            if (lot) {
                const { name, cost_cents, discounted_cost_cents, sell_price_cents } = lot;
                const { lotsAggState, lots } = result;
                lots.push({ id: lotId, name });
                // In lot extended_cost, extended_discounted_cost and ext_sell_price is same as
                // cost, discounted_cost and sell_price, because qty and per do not have any impact on them.
                if (cost_cents) lotsAggState.extendedCost += convertToDollar(cost_cents);
                if (discounted_cost_cents) {
                    lotsAggState.extendedDiscountCost += convertToDollar(discounted_cost_cents);
                }
                if (sell_price_cents) lotsAggState.extendedPrice += convertToDollar(sell_price_cents);
            }
            return result;
        },
        {
            lotsAggState: {
                extendedCost: 0,
                extendedDiscountCost: 0,
                extendedPrice: 0
            },
            lots: [] as TableItem['lots']
        }
    );

    const aggDataObj = manufacturers.reduce<TableItem>(
        (result, manufacturerData) => {
            manufacturerData.bods.forEach((bod) => {
                const { lotId, kit, extendedCost, extendedDiscountCost, extendedPrice } = bod;

                // get kit, if this is component
                const kitDetails = kit ? kitMap.get(kit) : undefined;

                // Only aggregate data of records without lotId.
                if (lotId || kitDetails?.lotId) return;

                // If bod is not part of Kit, use 1 as it won't change the multiplication
                const kitQuantity = kitDetails ? kitDetails.qty || 0 : 1;

                // Only aggregate data of non-kit Components or products.
                if (extendedCost) result.extendedCost += extendedCost * kitQuantity;
                if (extendedDiscountCost) {
                    result.extendedDiscountCost += extendedDiscountCost * kitQuantity;
                }
                if (extendedPrice) result.extendedPrice += extendedPrice * kitQuantity;
            });
            return result;
        },
        {
            ...lotsAggResult.lotsAggState, // initial data based on lots of this combined Item
            id: uniqueId,
            manufacturers: manufacturerNames, // list of manufacturers for this row,
            margin: 0,
            extendedRevenue: 0,
            lots: lotsAggResult.lots
        }
    );

    const { extendedDiscountCost, extendedPrice } = aggDataObj;
    // Calculate margin and extendedRevenue
    const margin = Number(((1 - extendedDiscountCost / extendedPrice) * 100).toFixed(2));
    aggDataObj.margin = Number.isFinite(margin) ? margin : 0;

    const extendedRevenue = extendedPrice - extendedDiscountCost;
    aggDataObj.extendedRevenue = Number.isFinite(extendedRevenue) ? extendedRevenue : 0;

    return aggDataObj;
};

export const getLotName = (lot: LotsResponse) => lot.name;

export function formatDollarAmount(params: ValueFormatterParams<TableItem, number>) {
    if (isNil(params.value)) return '';
    return getUsdFormattedString(params.value);
}

export const applyInitialSortStateToTable = (event: FirstDataRenderedEvent<TableItem>, sortField: SortPayload | null) => {
    if (isEmpty(sortField) || !isObjectLike(sortField)) return;

    const state = Object.entries(sortField).reduce<ColumnState[]>((acc, keyValPair) => {
        const [colId, sortProps] = keyValPair;
        const { sort, sortIndex } = sortProps || {};

        if (colId && sort && !isNil(sortIndex)) {
            acc.push({ colId, sort, sortIndex });
        }
        return acc;
    }, []);

    // set sort of saved columns, and reset other columns sort(if any)
    event.api.applyColumnState({
        state,
        defaultState: {
            // important to say 'null' as undefined means 'do nothing'
            sort: null
        }
    });
};

export const getSortedColumns = (columnsState: ColumnState[]) => {
    return columnsState.reduce<SortPayload>((acc, column) => {
        const { colId, sort, sortIndex } = column;
        if (colId && sort && !isNil(sortIndex)) {
            acc[colId] = { sort, sortIndex };
        }
        return acc;
    }, {});
};
